import { motion } from "framer-motion"
import { CircleX, Filter, SortAsc, Clock, TrendingUp, TrendingDown, Search, MapPin, User } from "lucide-react"
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

interface FilterState {
    courseName: string;
    instructorName: string;
    city: string;
    categories: string[];
}

interface Category {
    id: string;
    name: string;
    createdAt: string;
    updatedAt: string;
}

interface CoursesFilteringProps {
    onSort: (sortType: 'alphabetical' | 'date' | 'price-high' | 'price-low' | null) => void;
    onSearch: (filters: FilterState) => void;
    onClearAll: () => void;
    categories: Category[];
}

const CoursesFiltering = ({ onSort, onSearch, onClearAll, categories }: CoursesFilteringProps) => {
    const [searchParams] = useSearchParams();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [activeSorting, setActiveSorting] = useState<'alphabetical' | 'date' | 'price-high' | 'price-low' | null>(null);
    const [filters, setFilters] = useState<FilterState>({
        courseName: '',
        instructorName: '',
        city: '',
        categories: []
    });

    useEffect(() => {
        const categoryParam = searchParams.get('category');
        if (categoryParam && categories.length > 0) {
            const categoryId = categories.find(
                cat => cat.name.toLowerCase() === decodeURIComponent(categoryParam).toLowerCase()
            )?.id;

            if (categoryId) {
                const newFilters = {
                    ...filters,
                    categories: [categoryId]
                };
                setFilters(newFilters);
                onSearch(newFilters);
            }
        }
    }, [categories, searchParams, onSearch]);

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const handleSort = (sortType: 'alphabetical' | 'date' | 'price-high' | 'price-low') => {
        if (activeSorting === sortType) {
            setActiveSorting(null);
            onSort(null);
        } else {
            setActiveSorting(sortType);
            onSort(sortType);
        }
    };

    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const newFilters = { ...filters, [name]: value };
        setFilters(newFilters);
        onSearch(newFilters);
    };

    const handleCategoryChange = (categoryId: string) => {
        const newCategories = filters.categories.includes(categoryId)
            ? filters.categories.filter(id => id !== categoryId)
            : [...filters.categories, categoryId];

        const newFilters = { ...filters, categories: newCategories };
        setFilters(newFilters);
        onSearch(newFilters);
    };

    const handleClearAll = () => {
        setFilters({
            courseName: '',
            instructorName: '',
            city: '',
            categories: []
        });
        setActiveSorting(null);
        onClearAll();
    };

    const sidebarVariants = {
        open: { x: 0 },
        closed: { x: "-100%" },
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-8 sm:gap-10 gap-5 flex-wrap">
                <button onClick={toggleSidebar} className="flex items-center text-secondary gap-2">
                    <Filter className="sm:size-10 size-7" size={40} />
                    <p className="sm:text-lg font-bold">Filter Results</p>
                </button>

                <div className="flex flex-wrap sm:gap-4 gap-2.5">
                    <button 
                        onClick={() => handleSort('alphabetical')}
                        className={`flex items-center gap-2 order-1 sm:px-4 px-3 sm:text-base text-sm sm:py-2 py-1.5  rounded-full transition-all duration-300  ${
                            activeSorting === 'alphabetical' 
                                ? 'bg-secondary text-white' 
                                : 'bg-gray-100 text-gray-600'
                        }`}
                    >
                        <SortAsc className="sm:size-5 size-4" size={20} />
                        <span>A-Z</span>
                    </button>
                    <button 
                        onClick={() => handleSort('date')}
                        className={`flex items-center sm:order-2 order-3 gap-2 sm:px-4 px-3 sm:text-base text-sm sm:py-2 py-1.5  rounded-full transition-all duration-300  ${
                            activeSorting === 'date' 
                                ? 'bg-secondary text-white' 
                                : 'bg-gray-100 text-gray-600'
                        }`}
                    >
                        <Clock size={20} />
                        <span>Newest</span>
                    </button>
                    <button 
                        onClick={() => handleSort('price-high')}
                        className={`flex items-center gap-2 sm:order-3 order-2 sm:px-4 px-3 sm:text-base text-sm sm:py-2 py-1.5  rounded-full transition-all duration-300  ${
                            activeSorting === 'price-high' 
                                ? 'bg-secondary text-white' 
                                : 'bg-gray-100 text-gray-600'
                        }`}
                    >
                        <TrendingUp className="sm:size-5 size-4" size={20} />
                        <span>Price: High to Low</span>
                    </button>
                    <button 
                        onClick={() => handleSort('price-low')}
                        className={`flex items-center gap-2 order-4 sm:px-4 px-3 sm:text-base text-sm sm:py-2 py-1.5  rounded-full transition-all duration-300  ${
                            activeSorting === 'price-low' 
                                ? 'bg-secondary text-white' 
                                : 'bg-gray-100 text-gray-600'
                        }`}
                    >
                        <TrendingDown className="sm:size-5 size-4" size={20} />
                        <span>Price: Low to High</span>
                    </button>
                </div>
            </div>

            {isSidebarOpen && (
                <motion.div
                    className="fixed inset-0 bg-black bg-opacity-50 z-[9999999999999999]"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    onClick={toggleSidebar}
                />
            )}
            <motion.div
                className="flex flex-col bg-white fixed top-0 left-0 z-[9999999999999999] h-full shadow-lg p-5 md:w-[50%] w-[70%] overflow-y-auto"
                variants={sidebarVariants}
                initial="closed"
                animate={isSidebarOpen ? "open" : "closed"}
                transition={{
                    ease: "linear",
                    duration: 2,
                    x: { duration: 0.3 },
                }}
            >
                <div className="flex justify-between items-center mb-8">
                    <h2 className="sm:text-2xl text-xl font-bold text-secondary">Filter Courses</h2>
                    <button onClick={toggleSidebar}>
                        <CircleX className="text-gray-500 hover:text-secondary transition-colors" />
                    </button>
                </div>

                <div className="flex flex-col sm:gap-6 gap-4">
                    <div className="space-y-2">
                        <label htmlFor="courseName" className="block text-sm font-medium text-gray-700">
                            Course Name
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 sm:size-5 size-4" size={20} />
                            <input
                                id="courseName"
                                type="text"
                                name="courseName"
                                value={filters.courseName}
                                onChange={handleFilterChange}
                                placeholder="Enter course name to search..."
                                className="w-full pl-8 sm:pl-12 pr-4 sm:py-3 py-2 rounded-lg border border-gray-200 focus:border-secondary focus:ring-1 focus:ring-secondary outline-none transition-all sm:text-base text-xs"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="instructorName" className="block text-sm font-medium text-gray-700">
                            Instructor Name
                        </label>
                        <div className="relative">
                            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 sm:size-5 size-4" size={20} />
                            <input
                                id="instructorName"
                                type="text"
                                name="instructorName"
                                value={filters.instructorName}
                                onChange={handleFilterChange}
                                placeholder="Search by instructor's first or last name..."
                                className="w-full pl-8 sm:pl-12 pr-4 sm:py-3 py-2 rounded-lg border border-gray-200 focus:border-secondary focus:ring-1 focus:ring-secondary outline-none transition-all sm:text-base text-xs"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                            Location
                        </label>
                        <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 sm:size-5 size-4" size={20} />
                            <input
                                id="city"
                                type="text"
                                name="city"
                                value={filters.city}
                                onChange={handleFilterChange}
                                placeholder="Enter city name..."
                                className="w-full pl-8 sm:pl-12 pr-4 sm:py-3 py-2 rounded-lg border border-gray-200 focus:border-secondary focus:ring-1 focus:ring-secondary outline-none transition-all sm:text-base text-xs"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            Categories
                        </label>
                        <div className="flex flex-col sm:gap-2 bg-gray-50 sm:p-4 py-4 px-2 rounded-lg">
                            {categories.map((category) => (
                                <label 
                                    key={category.id} 
                                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 p-2 rounded-md transition-colors"
                                >
                                    <input
                                        type="checkbox"
                                        checked={filters.categories.includes(category.id)}
                                        onChange={() => handleCategoryChange(category.id)}
                                        className="sm:size-4 size-3 text-secondary border-gray-300 rounded focus:ring-secondary"
                                    />
                                    <span className="text-gray-700 sm:text-base text-xs">{category.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <button
                        onClick={handleClearAll}
                        className="mt-4 w-full bg-red-50 text-red-600 sm:py-3 py-2 sm:text-base text-sm rounded-lg font-medium hover:bg-red-100 transition-colors duration-300"
                    >
                        Clear All Filters
                    </button>
                </div>
            </motion.div>
        </div>
    )
}

export default CoursesFiltering